import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCreateOpportunity, useAccounts, useUsers, useContacts } from '@/hooks/use-api'
import { usePicklistOptions, usePicklistOptionPairs, useStages } from '@/hooks/use-picklists'
import { useUser } from '@/contexts/UserContext'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { ownerPickerOptions } from '@/lib/salesReps'
import type { Opportunity } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateOpportunityModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ownerId?: string // Pre-fill owner
  accountId?: string // Pre-fill account
  accountName?: string // Account display name for fallback
  contactId?: string // Pre-fill billing contact
  onCreated?: (opportunity: Opportunity) => void
}

export function CreateOpportunityModal({
  open,
  onOpenChange,
  ownerId,
  accountId,
  accountName,
  contactId,
  onCreated,
}: CreateOpportunityModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateOpportunity()
  const [accountSearch, setAccountSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const { data: resellerData } = useAccounts({
    account_type: 'Reseller',
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  } as any)
  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const { data: usersData } = useUsers({ limit: 10000 })
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const { data: contactsData } = useContacts({
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: selectedAccountId || undefined,
  })
  const currencyCodes = usePicklistOptions('general', 'currency_code')
  const saleTypes = usePicklistOptions('opportunity', 'sale_type')
  const leadSources = usePicklistOptions('opportunity', 'lead_source')
  const opportunityCategories = usePicklistOptions('opportunity', 'category')
  const opportunityTypes = usePicklistOptions('opportunity', 'type')
  const contractTermOptions = usePicklistOptionPairs('opportunity', 'contract_term')
  const { stages, getProbability } = useStages()

  const [formData, setFormData] = useState<Partial<Opportunity>>({
    name: '',
    currency_iso_code: 'CAD',
    account_id: '',
    owner_id: '',
    reseller__c: '',
    stage_name: 'Need Identified',
    sale_type__c: '',
    amount: 0,
    discount__c: undefined,
    type: '',
    opportunity_category__c: '',
    lead_source: '',
    close_date: '',
    lead_source_details__c: '',
    renewal_opportunity__c: '',
    description: '',
    billing_contact_id: '',
    po_number__c: '',
    memo__c: '',
    contract_term__c: undefined,
    contract_start_date__c: '',
    contract_end_date__c: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [probability, setProbability] = useState(10)
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false)

  // Update probability when stage changes
  useEffect(() => {
    if (formData.stage_name) {
      const prob = getProbability(formData.stage_name)
      setProbability(prob)
    }
  }, [formData.stage_name, getProbability])

  const accounts = accountsData?.data || []

  // Pre-populate owner, account, contact when modal opens
  useEffect(() => {
    if (open) {
      const prefilledAccount = accountId ? accounts.find((a) => a.id === accountId) : undefined
      setFormData((prev) => ({
        ...prev,
        owner_id: ownerId || currentUser?.id || prev.owner_id || '',
        account_id: accountId || prev.account_id || '',
        billing_contact_id: contactId || prev.billing_contact_id || '',
        currency_iso_code: prefilledAccount?.currency_iso_code || prev.currency_iso_code || 'CAD',
      }))
      if (accountId) {
        setSelectedAccountId(accountId)
      }
      setNameManuallyEdited(false)

      // Auto-seed opportunity name when account is prefilled.
      // nameManuallyEdited was just reset above, so the only guard we need is
      // "don't clobber a name that's already in state" (protects typed-name
      // carryover when the modal is reopened without form reset).
      if (accountId) {
        setFormData((prev) => {
          if (prev.name && prev.name.trim().length > 0) return prev
          const generated = generateOpportunityName(
            accountId,
            (prev.sale_type__c as string) || '',
            accountName
          )
          return generated ? { ...prev, name: generated } : prev
        })
      }
    }
  }, [open, ownerId, accountId, accountName, contactId, currentUser?.id, accounts])
  const resellerAccounts = resellerData?.data || []
  const users = usersData?.data || []
  const contacts = contactsData?.data || []

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Opportunity name is required'
    }

    if (!formData.account_id) {
      newErrors.account_id = 'Account is required'
    }

    if (!formData.owner_id) {
      newErrors.owner_id = 'Owner is required'
    }

    if (!formData.stage_name) {
      newErrors.stage_name = 'Stage is required'
    }

    if (!formData.close_date) {
      newErrors.close_date = 'Close date is required'
    }

    if (formData.amount === undefined || formData.amount === null || formData.amount < 0) {
      newErrors.amount = 'Amount must be a positive number'
    }

    if (!formData.lead_source) {
      newErrors.lead_source = 'Lead source is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await createMutation.mutateAsync(formData)

      toast({
        title: 'Success',
        description: 'Opportunity created successfully.',
      })

      if (onCreated && result?.id) {
        onCreated(result as Opportunity)
      }

      // Reset form and close modal
      setFormData({
        name: '',
        currency_iso_code: 'CAD',
        account_id: '',
        owner_id: ownerId || currentUser?.id || '',
        reseller__c: '',
        stage_name: 'Need Identified',
        sale_type__c: '',
        amount: 0,
        discount__c: undefined,
        type: '',
        opportunity_category__c: '',
        lead_source: '',
        close_date: '',
        lead_source_details__c: '',
        renewal_opportunity__c: '',
        description: '',
        billing_contact_id: '',
        po_number__c: '',
        memo__c: '',
        contract_term__c: undefined,
        contract_start_date__c: '',
        contract_end_date__c: '',
      })
      setErrors({})
      setNameManuallyEdited(false)
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create opportunity')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const computeContractEndDate = (startDate: string, termMonths: number): string => {
    const date = new Date(startDate + 'T00:00:00')
    date.setMonth(date.getMonth() + termMonths)
    date.setDate(date.getDate() - 1)
    // Format from LOCAL components — toISOString() would convert to UTC and can shift the day.
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const generateOpportunityName = (
    accountId: string,
    saleType: string,
    fallbackName?: string
  ): string => {
    const account = accounts.find((a) => a.id === accountId)
    const accountLabel = account?.name || fallbackName || ''
    const now = new Date()
    const month = now.toLocaleString('en-US', { month: 'long' })
    const year = now.getFullYear()
    const parts = [accountLabel, saleType, `${month}_${year}`].filter(Boolean)
    return parts.join(' - ')
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    if (field === 'name') {
      setNameManuallyEdited(true)
    }

    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      // Clear billing contact when account changes, update account filter and currency
      if (field === 'account_id') {
        updated.billing_contact_id = ''
        setSelectedAccountId(value as string)
        const selectedAccount = accounts.find((a) => a.id === value)
        if (selectedAccount?.currency_iso_code) {
          updated.currency_iso_code = selectedAccount.currency_iso_code
        }
      }
      // Auto-populate opportunity name when account or sale type changes
      if ((field === 'account_id' || field === 'sale_type__c') && !nameManuallyEdited) {
        const acctId = field === 'account_id' ? (value as string) : (prev.account_id as string)
        const saleType =
          field === 'sale_type__c' ? (value as string) : (prev.sale_type__c as string)
        if (acctId) {
          updated.name = generateOpportunityName(acctId, saleType || '', accountName)
        }
      }
      // Auto-calculate contract end date
      if (field === 'contract_term__c' || field === 'contract_start_date__c') {
        const term = field === 'contract_term__c' ? value : prev.contract_term__c
        const start = field === 'contract_start_date__c' ? value : prev.contract_start_date__c
        if (term && start) {
          updated.contract_end_date__c = computeContractEndDate(start as string, term as number)
        }
      }
      return updated
    })
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const selectClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-opportunity-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Opportunity</DialogTitle>
          <DialogDescription id="create-opportunity-description">
            Fill in the details to create a new opportunity. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* About Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              About
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Opportunity Name */}
              <div>
                <Label htmlFor="name">Opportunity Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter opportunity name"
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Currency */}
              <div>
                <Label htmlFor="currency">Opportunity Currency</Label>
                <select
                  id="currency"
                  value={formData.currency_iso_code}
                  onChange={(e) => handleChange('currency_iso_code', e.target.value)}
                  className={selectClassName}
                  aria-label="Opportunity currency"
                >
                  {currencyCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Account */}
              <div>
                <Label htmlFor="account_id">Account Name *</Label>
                <RecordCombobox
                  id="account_id"
                  records={accounts}
                  value={formData.account_id as string}
                  onValueChange={(value) => handleChange('account_id', value)}
                  placeholder="Select account..."
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No accounts found."
                  displayField={(acc) => acc.name || ''}
                  aria-label="Account Name"
                  onSearchChange={setAccountSearch}
                  isLoading={accountsLoading}
                  selectedLabel={accountName}
                  totalCount={accountsData?.total}
                />
                {errors.account_id && (
                  <p id="account-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.account_id}
                  </p>
                )}
              </div>

              {/* Owner */}
              <div>
                <Label htmlFor="owner_id">Opportunity Owner *</Label>
                <select
                  id="owner_id"
                  value={formData.owner_id}
                  onChange={(e) => handleChange('owner_id', e.target.value)}
                  className={selectClassName}
                  aria-required="true"
                  aria-invalid={!!errors.owner_id}
                  aria-describedby={errors.owner_id ? 'owner-error' : undefined}
                >
                  <option value="">Select an owner...</option>
                  {ownerPickerOptions(users, formData.owner_id).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {errors.owner_id && (
                  <p id="owner-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.owner_id}
                  </p>
                )}
              </div>

              {/* Reseller */}
              <div>
                <Label htmlFor="reseller">Reseller</Label>
                <select
                  id="reseller"
                  value={formData.reseller__c}
                  onChange={(e) => handleChange('reseller__c', e.target.value)}
                  className={selectClassName}
                  aria-label="Reseller account"
                >
                  <option value="">Select reseller...</option>
                  {[...resellerAccounts]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Stage */}
              <div>
                <Label htmlFor="stage_name">Stage *</Label>
                <select
                  id="stage_name"
                  value={formData.stage_name}
                  onChange={(e) => handleChange('stage_name', e.target.value)}
                  className={selectClassName}
                  aria-required="true"
                  aria-invalid={!!errors.stage_name}
                  aria-describedby="stage-description"
                >
                  {stages.map((stage) => (
                    <option key={stage.value} value={stage.value}>
                      {stage.label}
                    </option>
                  ))}
                </select>
                <p id="stage-description" className="text-sm text-muted-foreground mt-1">
                  Probability: {probability}%
                </p>
                {errors.stage_name && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.stage_name}
                  </p>
                )}
              </div>

              {/* Sale Type */}
              <div>
                <Label htmlFor="sale_type">Sale Type</Label>
                <select
                  id="sale_type"
                  value={formData.sale_type__c}
                  onChange={(e) => handleChange('sale_type__c', e.target.value)}
                  className={selectClassName}
                  aria-label="Sale type"
                >
                  <option value="">Select sale type...</option>
                  {saleTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Opportunity Category */}
              <div>
                <Label htmlFor="opportunity_category">Opportunity Category</Label>
                <select
                  id="opportunity_category"
                  value={formData.opportunity_category__c}
                  onChange={(e) => handleChange('opportunity_category__c', e.target.value)}
                  className={selectClassName}
                  aria-label="Opportunity category"
                >
                  <option value="">Select category...</option>
                  {opportunityCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  aria-required="true"
                  aria-invalid={!!errors.amount}
                  aria-describedby={errors.amount ? 'amount-error' : undefined}
                />
                {errors.amount && (
                  <p id="amount-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.amount}
                  </p>
                )}
              </div>

              {/* Discount */}
              <div>
                <Label htmlFor="discount">Discount (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.discount__c ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'discount__c',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  placeholder="0"
                  aria-label="Discount percentage"
                />
              </div>

              {/* Type */}
              <div>
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className={selectClassName}
                  aria-label="Opportunity type"
                >
                  <option value="">Select type...</option>
                  {opportunityTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lead Source */}
              <div>
                <Label htmlFor="lead_source">Lead Source *</Label>
                <select
                  id="lead_source"
                  value={formData.lead_source}
                  onChange={(e) => handleChange('lead_source', e.target.value)}
                  className={selectClassName}
                  aria-label="Lead source"
                  aria-required="true"
                  aria-invalid={!!errors.lead_source}
                  aria-describedby={errors.lead_source ? 'lead-source-error' : undefined}
                >
                  <option value="">Select lead source...</option>
                  {leadSources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
                {errors.lead_source && (
                  <p id="lead-source-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.lead_source}
                  </p>
                )}
              </div>

              {/* Close Date */}
              <div>
                <Label htmlFor="close_date">Close Date *</Label>
                <DatePicker
                  id="close_date"
                  value={formData.close_date}
                  onChange={(v) => handleChange('close_date', v ?? '')}
                  aria-label="Close Date"
                  aria-required="true"
                  aria-invalid={!!errors.close_date}
                  aria-describedby={errors.close_date ? 'close-date-error' : undefined}
                />
                {errors.close_date && (
                  <p id="close-date-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.close_date}
                  </p>
                )}
              </div>

              {/* Lead Source Details */}
              <div>
                <Label htmlFor="lead_source_details">Lead Source Details</Label>
                <Input
                  id="lead_source_details"
                  value={formData.lead_source_details__c}
                  onChange={(e) => handleChange('lead_source_details__c', e.target.value)}
                  placeholder="Additional lead source info..."
                  aria-label="Lead source details"
                />
              </div>

              {/* Renewal Opportunity */}
              <div className="flex items-center space-x-2 pt-6">
                <input
                  id="renewal_opportunity"
                  type="checkbox"
                  checked={formData.renewal_opportunity__c === 'true'}
                  onChange={(e) =>
                    handleChange('renewal_opportunity__c', e.target.checked ? 'true' : '')
                  }
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  aria-label="Renewal opportunity"
                />
                <Label htmlFor="renewal_opportunity" className="text-sm font-normal cursor-pointer">
                  Renewal Opportunity
                </Label>
              </div>
            </div>

            {/* Description - full width */}
            <div className="mt-4">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Enter opportunity description..."
                aria-label="Opportunity description"
              />
            </div>
          </div>

          {/* Additional Details Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Additional Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Billing Contact */}
              <div>
                <Label htmlFor="billing_contact">Billing Contact</Label>
                <select
                  id="billing_contact"
                  value={formData.billing_contact_id}
                  onChange={(e) => handleChange('billing_contact_id', e.target.value)}
                  className={selectClassName}
                  aria-label="Billing contact"
                >
                  <option value="">Select contact...</option>
                  {[...contacts]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* PO Number */}
              <div>
                <Label htmlFor="po_number">PO Number</Label>
                <Input
                  id="po_number"
                  value={formData.po_number__c}
                  onChange={(e) => handleChange('po_number__c', e.target.value)}
                  placeholder="Enter PO number..."
                  aria-label="PO number"
                />
              </div>
            </div>

            {/* Invoice Memo - full width */}
            <div className="mt-4">
              <Label htmlFor="memo">Invoice Memo</Label>
              <textarea
                id="memo"
                value={formData.memo__c}
                onChange={(e) => handleChange('memo__c', e.target.value)}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Enter invoice memo..."
                aria-label="Invoice memo"
              />
            </div>
          </div>

          {/* Contract Details Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Contract Details
            </h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Contract Term */}
              <div>
                <Label htmlFor="contract_term">Contract Term</Label>
                <select
                  id="contract_term"
                  value={formData.contract_term__c ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'contract_term__c',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Contract term"
                >
                  <option value="">Select...</option>
                  {contractTermOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contract Start Date */}
              <div>
                <Label htmlFor="contract_start_date">Contract Start Date</Label>
                <DatePicker
                  id="contract_start_date"
                  value={formData.contract_start_date__c}
                  onChange={(v) => handleChange('contract_start_date__c', v ?? '')}
                  aria-label="Contract start date"
                />
              </div>

              {/* Contract End Date */}
              <div>
                <Label htmlFor="contract_end_date">Contract End Date</Label>
                <DatePicker
                  id="contract_end_date"
                  value={formData.contract_end_date__c}
                  onChange={(v) => handleChange('contract_end_date__c', v ?? '')}
                  aria-label="Contract end date"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
              aria-label="Cancel and close modal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              aria-label={
                createMutation.isPending ? 'Creating opportunity...' : 'Create opportunity'
              }
            >
              {createMutation.isPending ? 'Creating...' : 'Create Opportunity'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
